import {
	ContentExpander,
	Grid,
	NumberField,
	Spacer
} from '@components';
import { useClearRCSettingsError, useRateCardModifiersState } from '@hooks';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import {
	AllRateCardFuelLevyModifiers,
	AllRateCardSettingFuelLevyModifiers,
	RateCardFormErrors,
	RateCardSettingsTypes, RateCardTypes
} from '@state';
import { RateCardType, TRateCardModifierSet } from '@types';
import { getRateCardIndex, removeLeadingZeros } from '@utils';
import { useRecoilValue } from 'recoil';
import styles from './Modifiers.module.scss';
import { ModifierBadge } from './ModifierBadge';

type TProps = {
	isSettings?: boolean;
	type: RateCardType;
	isDisabled?: boolean;
	itemTypeId?: string;
}

export const EquipmentModifierPanel = (props: TProps) => {
	const {
		isSettings,
		type,
		isDisabled = false,
		itemTypeId,
	} = props;
	const [rateCardModifiers, setRateCardModifiers] = useRateCardModifiersState();
	const modifiers = isSettings ? rateCardModifiers.settings : rateCardModifiers.rateCard;
	const rcFormErrors = useRecoilValue(RateCardFormErrors);
	const rateCardSettingsTypes = useRecoilValue(RateCardSettingsTypes);

	const allRateCardModifiers = useRecoilValue(AllRateCardFuelLevyModifiers);
	const allRateCardSettingModifiers = useRecoilValue(AllRateCardSettingFuelLevyModifiers);
	const fuelLevyModifiers = isSettings ? allRateCardSettingModifiers : allRateCardModifiers;

	const rateCardTypes = useRecoilValue(RateCardTypes);
	const rateCardTypeIndex = getRateCardIndex({
		rateCardSettingsTypes,
		rateCardTypes,
		itemTypeId,
		isSettings: !!isSettings,
		type
	});
	const errorString = isSettings ? 'rate_card_types' : 'rate_card_prices';
	const clearRCSettingsError = useClearRCSettingsError();

	const updateEquipmentModifier = (
		modifierKey: keyof TRateCardModifierSet,
		newValue: string
	) => {
		const update = {
			...modifiers,
			[modifierKey]: {
				type: 'FIXED',
				value: newValue,
				attracts_fuel_levy: !!fuelLevyModifiers?.[modifierKey]?.attracts_fuel_levy
			}
		} as TRateCardModifierSet;
		const newModifiers = {
			settings: isSettings ? update : undefined,
			rateCard: !isSettings ? update : undefined
		}
		setRateCardModifiers(newModifiers);
		// @ts-ignore
		if (rcFormErrors?.[errorString]?.[rateCardTypeIndex]?.modifier_set[modifierKey]) {
			clearRCSettingsError(`${errorString}.${rateCardTypeIndex}.modifier_set.${modifierKey}.value`)
		}
	}

	const handleOnBlur = (
		modifierKey: keyof TRateCardModifierSet,
		newValue: string
	) => {
		if (newValue.startsWith('0') && !newValue.startsWith('0.')) {
			const value = removeLeadingZeros(newValue);
			updateEquipmentModifier(modifierKey, value);
		}
		if (newValue.startsWith('.')) {
			updateEquipmentModifier(modifierKey, `0${newValue}`)
		}
	}

	const hasError = (key: string) => {
		// @ts-ignore
		return rcFormErrors?.[errorString]?.[rateCardTypeIndex]?.modifier_set?.[key]?.value?._errors?.[0] || '';
	}

	const getSectionErrors = () => {
		const fieldsWithPossibleError = ['pallet_jack', 'pallet_exchange', 'pallet_transfer']
		return fieldsWithPossibleError.map((field) =>
			hasError(field)
		).some((field) => field.length > 0);
	}

	const getConfiguredCount = () => {
		const countedFields = ['pallet_jack', 'pallet_exchange', 'pallet_transfer'] as const;
		return countedFields.reduce((count, field) => {
			const value = modifiers?.[field]?.value;
			return parseFloat(`${value ?? ''}`) > 0 ? count + 1 : count;
		}, 0);
	}

	if (!modifiers) return

	return (
		<ContentExpander
			title={<div className={styles.expanderTitle}>Equipment
				<ModifierBadge count={getConfiguredCount()} />
				{
					getSectionErrors() ?
						<span className={styles.expanderErrorContainer}>
							<ErrorOutlineOutlinedIcon />
						</span>	: null
				}
			</div>
			}
			showSpacer
			expanded={false}
			arrowsAsExpanderIcon
			iconColor='var(--neutral-06)'
		>
			<div className={styles.expanderContent}>
				<Grid columns={2} columnTemplate={'repeat(2, 1fr)'} gap={'16px'} alignItems='start'>
					<NumberField
						name={'pallet_jack'}
						value={`${modifiers.pallet_jack.value}`}
						onChange={(e) => updateEquipmentModifier('pallet_jack', e.target.value)}
						leftModule={<span className={styles.prefix}>$</span>}
						label={'Pallet jack'}
						keepErrorSpace={false}
						errorMessage={hasError('pallet_jack')}
						hasError={!!hasError('pallet_jack')}
						disabled={isDisabled}
						min={'0'}
						onBlur={(e) => handleOnBlur('pallet_jack', e.target.value)}
					/>
					{/* <NumberField
						name={'hand_unload'}
						value={`${modifiers.hand_unload.value}`}
						onChange={(e) => updateEquipmentModifier('hand_unload', e.target.value)}
						leftModule={<span className={styles.prefix}>$</span>}
						label={'Hand unload'}
						keepErrorSpace={false}
						errorMessage={hasError('hand_unload')}
						hasError={!!hasError('hand_unload')}
						disabled={isDisabled}
						min={'0'}
						onBlur={(e) => handleOnBlur('hand_unload', e.target.value)}
					/> */}
					{/* <NumberField
						name={'forklift'}
						value={`${modifiers.forklift.value}`}
						onChange={(e) => updateEquipmentModifier('forklift', e.target.value)}
						leftModule={<span className={styles.prefix}>$</span>}
						label={'Forklift'}
						keepErrorSpace={false}
						errorMessage={hasError('forklift')}
						hasError={!!hasError('forklift')}
						disabled={isDisabled}
						min={'0'}
						onBlur={(e) => handleOnBlur('forklift', e.target.value)}
					/> */}
					{/* <NumberField
						name={'not_moveable_by_forklift'}
						value={`${modifiers.not_moveable_by_forklift.value}`}
						onChange={(e) => updateEquipmentModifier('not_moveable_by_forklift', e.target.value)}
						leftModule={<span className={styles.prefix}>$</span>}
						label={'Not moveable by forklift'}
						keepErrorSpace={false}
						disabled={isDisabled}
						min={'0'}
						onBlur={(e) => handleOnBlur('not_moveable_by_forklift', e.target.value)}
					/> */}
					<NumberField
						name={'pallet_exchange'}
						value={`${modifiers.pallet_exchange.value}`}
						onChange={(e) => updateEquipmentModifier('pallet_exchange', e.target.value)}
						leftModule={<span className={styles.prefix}>$</span>}
						label={'Pallet exchange'}
						keepErrorSpace={false}
						errorMessage={hasError('pallet_exchange')}
						hasError={!!hasError('pallet_exchange')}
						disabled={isDisabled}
						min={'0'}
						onBlur={(e) => handleOnBlur('pallet_exchange', e.target.value)}
					/>
					<NumberField
						name={'pallet_transfer'}
						value={`${modifiers.pallet_transfer.value}`}
						onChange={(e) => updateEquipmentModifier('pallet_transfer', e.target.value)}
						leftModule={<span className={styles.prefix}>$</span>}
						label={'Pallet transfer'}
						keepErrorSpace={false}
						errorMessage={hasError('pallet_transfer')}
						hasError={!!hasError('pallet_transfer')}
						disabled={isDisabled}
						min={'0'}
						onBlur={(e) => handleOnBlur('pallet_transfer', e.target.value)}
					/>
				</Grid>
				<Spacer size={5} />
			</div>
		</ContentExpander>
	);
}